/**
 * ReferralSection - Referral program dashboard for LTD holders
 * Shows unique referral code, share link, stats, and earnings.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Gift, Copy, Users, Coins, Share2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReferralSection = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferral();
  }, []);

  const fetchReferral = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/stripe/referral`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      // Not an LTD holder or error
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    if (data?.referral_code) {
      navigator.clipboard.writeText(data.referral_code);
      toast.success('Referral code copied!');
    }
  };

  const copyLink = () => {
    if (data?.referral_code) {
      const link = `${window.location.origin}/register?ref=${data.referral_code}`;
      navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    }
  };

  if (loading || !data?.has_referral) return null;

  return (
    <Card data-testid="referral-section-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Gift className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
          Referral Program
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-500">
          Share your unique code with other coaches. You earn <strong>10%</strong> of every referral's payment.
        </p>

        {/* Referral code display */}
        <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex-1">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider">Your Code</p>
            <p className="text-xl font-black font-mono text-amber-800 tracking-wider" data-testid="referral-code-display">
              {data.referral_code}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100 gap-1"
            onClick={copyCode}
            data-testid="copy-referral-code"
          >
            <Copy className="w-3.5 h-3.5" /> Copy
          </Button>
        </div>

        {/* Share link */}
        <Button
          variant="outline"
          className="w-full rounded-xl gap-2 text-sm border-gray-200"
          onClick={copyLink}
          data-testid="copy-referral-link"
        >
          <Share2 className="w-4 h-4" /> Copy Referral Link
        </Button>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-50 rounded-xl text-center">
            <Users className="w-5 h-5 text-gray-400 mx-auto mb-1" />
            <p className="text-2xl font-black text-gray-800" data-testid="referral-count">{data.total_referrals}</p>
            <p className="text-[10px] text-gray-500 font-medium">Referrals</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-xl text-center">
            <Coins className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
            <p className="text-2xl font-black text-emerald-700" data-testid="referral-earnings">&pound;{data.total_earnings.toFixed(2)}</p>
            <p className="text-[10px] text-gray-500 font-medium">Earned</p>
          </div>
        </div>

        {/* Recent referrals */}
        {data.referrals.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-2">Recent Referrals</p>
            <div className="space-y-1.5">
              {data.referrals.slice(0, 5).map((ref) => (
                <div key={ref.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                  <span className="text-gray-700 font-medium">{ref.referred_name}</span>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-400 capitalize">{ref.plan_id}</span>
                    <span className="font-bold text-emerald-600">+&pound;{ref.earning.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
